import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheckIcon, ClockIcon, UsersIcon, AlertTriangleIcon, CheckCircleIcon, FileTextIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const ServicePolicy = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.servicePolicy.title}</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t.servicePolicy.subtitle}
          </p>
        </div>

        {/* Visão Geral */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center dark:text-white">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              {t.servicePolicy.qualityCommitment}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {t.servicePolicy.introParagraph}
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">99.5%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.availability}</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">4.8/5</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.satisfaction}</div>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">24/7</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.forCritical}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Escopo do Serviço */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center dark:text-white">
              <UsersIcon className="h-5 w-5 mr-2" />
              {t.servicePolicy.scopeTitle}
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.servicePolicy.scopeDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 text-green-600 dark:text-green-400">✅ {t.servicePolicy.included}</h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li>• {t.servicePolicy.includedItem1}</li>
                  <li>• {t.servicePolicy.includedItem2}</li>
                  <li>• {t.servicePolicy.includedItem3}</li>
                  <li>• {t.servicePolicy.includedItem4}</li>
                  <li>• {t.servicePolicy.includedItem5}</li>
                  <li>• {t.servicePolicy.includedItem6}</li>
                  <li>• {t.servicePolicy.includedItem7}</li>
                  <li>• {t.servicePolicy.includedItem8}</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 text-red-600 dark:text-red-400">❌ {t.servicePolicy.excluded}</h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li>• {t.servicePolicy.excludedItem1}</li>
                  <li>• {t.servicePolicy.excludedItem2}</li>
                  <li>• {t.servicePolicy.excludedItem3}</li>
                  <li>• {t.servicePolicy.excludedItem4}</li>
                  <li>• {t.servicePolicy.excludedItem5}</li>
                  <li>• {t.servicePolicy.excludedItem6}</li>
                  <li>• {t.servicePolicy.excludedItem7}</li>
                  <li>• {t.servicePolicy.excludedItem8}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Horários de Atendimento */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center dark:text-white">
              <ClockIcon className="h-5 w-5 mr-2" />
              {t.servicePolicy.serviceHoursTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.businessHours}</h3>
                <div className="space-y-2 text-sm dark:text-gray-300">
                  <div className="flex justify-between">
                    <span>{t.servicePolicy.weekdays}</span>
                    <span className="font-medium">{t.servicePolicy.weekdaysTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.servicePolicy.saturday}</span>
                    <span className="font-medium">{t.servicePolicy.saturdayTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.servicePolicy.sundayHolidays}</span>
                    <span className="font-medium">{t.servicePolicy.sundayHolidaysTime}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.support247Title}</h3>
                <div className="space-y-2 text-sm">
                  <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 mb-2">{t.servicePolicy.criticalTicketsBadge}</Badge>
                  <p className="text-gray-700 dark:text-gray-300">
                    {t.servicePolicy.criticalTicketsDesc}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Responsabilidades */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-blue-600 dark:text-blue-400">{t.servicePolicy.responsibilitiesTecit}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm dark:text-gray-300">
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit1}</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit2}</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit3}</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit4}</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit5}</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respTecit6}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-600 dark:text-orange-400">{t.servicePolicy.responsibilitiesUser}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm dark:text-gray-300">
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser1}</span>
              </div>
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser2}</span>
              </div>
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser3}</span>
              </div>
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser4}</span>
              </div>
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser5}</span>
              </div>
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{t.servicePolicy.respUser6}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Processo de Escalação */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.servicePolicy.escalationTitle}</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.servicePolicy.escalationDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <h4 className="font-medium dark:text-gray-100">{t.servicePolicy.escalationAuto}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.escalationAutoDesc}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <h4 className="font-medium dark:text-gray-100">{t.servicePolicy.escalationTech}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.escalationTechDesc}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <h4 className="font-medium dark:text-gray-100">{t.servicePolicy.escalationMgmt}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t.servicePolicy.escalationMgmtDesc}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medição de Performance */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center dark:text-white">
              <FileTextIcon className="h-5 w-5 mr-2" />
              {t.servicePolicy.performanceIndicators}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.mainKpis}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm dark:text-gray-300">{t.servicePolicy.slaCompliance}</span>
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">≥ 95%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm dark:text-gray-300">{t.servicePolicy.firstResolution}</span>
                    <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">≥ 75%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm dark:text-gray-300">{t.servicePolicy.userSatisfaction}</span>
                    <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">≥ 4.5/5</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm dark:text-gray-300">{t.servicePolicy.avgResponseTime}</span>
                    <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">≤ 2h</Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.reportsTitle}</h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li>• {t.servicePolicy.reportItem1}</li>
                  <li>• {t.servicePolicy.reportItem2}</li>
                  <li>• {t.servicePolicy.reportItem3}</li>
                  <li>• {t.servicePolicy.reportItem4}</li>
                  <li>• {t.servicePolicy.reportItem5}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contato e Feedback */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.servicePolicy.contactFeedbackTitle}</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.servicePolicy.contactFeedbackDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.contactChannels}</h3>
                <div className="space-y-2 text-sm dark:text-gray-300">
                  <p>📧 <strong>{t.servicePolicy.emailLabel}</strong> suporte@tecitbettersolution.com.br</p>
                  <p>📞 <strong>{t.servicePolicy.phoneLabel}</strong> (11) 98546-4552</p>
                  <p>💬 <strong>{t.servicePolicy.chatLabel}</strong> {t.servicePolicy.chatAvailable}</p>
                  <p>🎫 <strong>{t.servicePolicy.systemLabel}</strong> {t.servicePolicy.systemPortal}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 dark:text-gray-200">{t.servicePolicy.complaintsSuggestions}</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  {t.servicePolicy.complaintsParagraph}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t.servicePolicy.responseTimeNote}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default ServicePolicy;
